/**
 * /grooves — staff review of Grooves songs that are not public (migration
 * 0532; founder 2026-11-02: every Postopia staff member sees them, "watch and
 * listen", discarded takes included). Nothing here changes a take. Each play is
 * logged by the worker under the Access email of whoever pressed it.
 * The same review is in the app and on the web (Music menu, @postopia team).
 */
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GROOVE_REVIEW_STATUSES, parseServerDate, type GrooveReviewItem, type GrooveReviewSummary } from '@postopia/shared';
import { AdminShell } from '../components/AdminShell';
import { groovesApi } from '../lib/groovesApi';

export const Route = createFileRoute('/grooves')({
  component: GroovesRoute,
});

function GroovesRoute() {
  return (
    <AdminShell mode="feature" featureName="Grooves">
      <GroovesConsole />
    </AdminShell>
  );
}

const STATUS_LABEL: Record<string, string> = {
  ready: 'Not published',
  rejected: 'Refused',
  failed: 'Failed',
  discarded: 'Discarded',
  expired: 'Expired',
  queued: 'Making',
};
const REASON_LABEL: Record<string, string> = {
  artist: 'Imitates a named artist',
  copied: 'Copied lyrics',
  harmful: 'Harmful',
  person: 'About a private person',
};

const chip = (on: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  borderRadius: 999,
  border: `1px solid ${on ? 'var(--c-fg-default)' : 'var(--c-border-hairline)'}`,
  background: on ? 'var(--c-fg-default)' : 'var(--c-bg-surface)',
  color: on ? 'var(--c-bg-page)' : 'var(--c-fg-default)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
});

function when(iso: string): string {
  const d = parseServerDate(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso;
}

function GroovesConsole() {
  const [status, setStatus] = useState<string>('ready');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<GrooveReviewItem[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [summary, setSummary] = useState<GrooveReviewSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audio, setAudio] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const seq = useRef(0);

  const load = useCallback(
    async (before?: string) => {
      const mine = ++seq.current;
      if (!before) setItems(null);
      setError(null);
      try {
        const r = await groovesApi.list({ status: status === 'all' ? undefined : status, q: query.trim() || undefined, before });
        if (mine !== seq.current) return;
        setItems((prev) => (before && prev ? [...prev, ...r.items] : r.items));
        setNext(r.next_before);
      } catch (e) {
        if (mine === seq.current) {
          setError(e instanceof Error ? e.message : 'Could not load.');
          setItems((prev) => prev ?? []);
        }
      }
    },
    [status, query],
  );

  useEffect(() => {
    const h = setTimeout(() => void load(), query ? 350 : 0);
    return () => clearTimeout(h);
  }, [load, query]);
  useEffect(() => {
    groovesApi.summary().then(setSummary).catch(() => setSummary(null));
  }, []);

  const listen = async (g: GrooveReviewItem) => {
    try {
      const r = await groovesApi.listen(g.id);
      setAudio((a) => ({ ...a, [g.id]: r.audio_url }));
      setItems((prev) => prev?.map((x) => (x.id === g.id ? { ...x, listens: x.listens + 1 } : x)) ?? prev);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not play this song.');
    }
  };

  const made = summary ? Object.values(summary.today).reduce((a, b) => a + (b ?? 0), 0) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 900 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--c-fg-secondary)' }}>
        Songs made with Grooves that are not public. Watch and listen only; every play is logged with your email.
        Ready takes lose their audio 30 days after they were made.
      </p>
      {summary ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, fontWeight: 600 }}>
          <span>{made} made today (Eastern)</span>
          <span>{summary.waiting} waiting to publish</span>
          <span>{summary.today.rejected ?? 0} refused today</span>
          <span>Spent today ${summary.spent_today_usd.toFixed(2)}</span>
          {Object.entries(summary.rejected_by_reason).map(([r, n]) => (
            <span key={r} style={{ color: '#C7563E' }}>
              {REASON_LABEL[r] ?? r}: {n}
            </span>
          ))}
        </div>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {['all', ...GROOVE_REVIEW_STATUSES].map((s) => (
          <button key={s} type="button" style={chip(s === status)} onClick={() => setStatus(s)}>
            {s === 'all' ? 'All' : STATUS_LABEL[s]}
          </button>
        ))}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value.slice(0, 100))}
          placeholder="Search titles and lyrics"
          style={{
            flex: '1 1 220px',
            padding: '6px 12px',
            borderRadius: 8,
            border: '1px solid var(--c-border-hairline)',
            background: 'var(--c-bg-surface)',
            color: 'var(--c-fg-default)',
            fontSize: 13,
          }}
        />
      </div>
      {error ? (
        <p role="alert" style={{ margin: 0, fontSize: 13, color: '#C7563E' }}>
          {error}
        </p>
      ) : null}
      {items === null ? (
        <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>No songs here.</p>
      ) : (
        items.map((g) => {
          const reason = g.error?.startsWith('content:') ? g.error.slice(8) : null;
          return (
            <article
              key={g.id}
              style={{
                padding: 14,
                borderRadius: 8,
                border: '1px solid var(--c-border-hairline)',
                background: 'var(--c-bg-surface)',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 15 }}>{g.title || 'Untitled'}</strong>
                <span style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>
                  {g.user.display_name || g.user.handle} @{g.user.handle}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: g.status === 'rejected' ? '#C7563E' : 'var(--c-fg-secondary)' }}>
                  {STATUS_LABEL[g.status] ?? g.status}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                {when(g.created_at)} · Take {g.take} · {Math.floor(g.duration_s / 60)}:{String(g.duration_s % 60).padStart(2, '0')} ·{' '}
                {g.provider}/{g.model} · ${g.cost_usd.toFixed(4)}
                {g.instrumental ? ' · Instrumental' : ''}
                {g.expires_at ? ` · deleted ${when(g.expires_at)} if not published` : ''}
                {g.listens ? ` · played ${g.listens}× by staff` : ''}
              </div>
              <div style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>{g.style}</div>
              {reason ? <div style={{ fontSize: 12, fontWeight: 600, color: '#C7563E' }}>{REASON_LABEL[reason] ?? reason}</div> : null}
              {g.error && !reason ? <div style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>{g.error}</div> : null}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {g.has_audio ? (
                  audio[g.id] ? (
                    <audio controls autoPlay src={audio[g.id]} style={{ height: 36 }} />
                  ) : (
                    <button type="button" style={chip(false)} onClick={() => void listen(g)}>
                      Play
                    </button>
                  )
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                    {g.status === 'discarded' || g.status === 'expired' ? 'Audio deleted' : 'No audio'}
                  </span>
                )}
                {g.lyrics ? (
                  <button type="button" style={chip(!!open[g.id])} onClick={() => setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))}>
                    {open[g.id] ? 'Hide lyrics' : 'Lyrics'}
                  </button>
                ) : null}
              </div>
              {open[g.id] && g.lyrics ? (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, lineHeight: '19px' }}>{g.lyrics}</pre>
              ) : null}
            </article>
          );
        })
      )}
      {next ? (
        <button type="button" style={chip(false)} onClick={() => void load(next)}>
          Load more
        </button>
      ) : null}
    </div>
  );
}
